// Customizable Area Start
import React from "react";
import { Box, BoxProps, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import LandingPageController, {
  Props,
  configJSON,
  ArtistStep,
  GET_ARTISTS_STEPS,
} from "./LandingPageController.web";
import NavigationMenu from "../../navigationmenu/src/NavigationMenu.web";
import { FireIcon, StepsIcon, TestimonialsIcon } from "./assets";
import ArtistStepsCard from "../../../components/src/ArtistStepsCard.web";
import LandingBannerCarousel from "../../../components/src/LandingBannerCarousel.web";
import LandingArtistCarousel from "../../../components/src/LandingArtistCarousel.web";
import LandingTestimonialsCarousel from "../../../components/src/LandingTestimonialsCarousel.web";
import AppFooter from "../../../components/src/AppFooter.web";
import Loader from "../../../components/src/Loader.web";
import FullPageLoader from "../../../components/src/FullPageLoader.web";
import CustomEmptyResultInfo from "../../../components/src/CustomEmptyResultInfo.web";
import GoogleAd from "../../../components/src/GoogleAd.web";

interface ArtistsSliderWrapperProps extends BoxProps {
  isEmpty?: boolean;
}
// Customizable Area End

export default class LandingPage extends LandingPageController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  render() {
    // Customizable Area Start
    const { navigation } = this.props;
    const {
      isGettingBanners,
      isGettingArtists,
      isGettingTestimonials,
      landingBanners,
      trendingArtists,
      testimonials,
    } = this.state;

    return (
      <>
        <FullPageLoader isLoading={isGettingBanners} />
        <NavigationMenu id="" navigation={navigation} />
        <Box bgcolor="#EFF6FF">
          <BannerSliderWrapper>
            <BannerSliderInnerWrapper>
              <LandingBannerCarousel
                data-test-id="banner-carousel"
                bannerSliderData={landingBanners}
              />
            </BannerSliderInnerWrapper>
          </BannerSliderWrapper>

          <TrendingArtistsWrapper component="section">
            <SectionHeadingWrapper>
              <img src={FireIcon} alt="Icon" />
              <CustomHeading data-test-id="trending-artists">
                {configJSON.trendingArtistsText}
              </CustomHeading>
              <SeeMoreButton
                data-test-id="see-more"
                disabled={isGettingArtists}
                onClick={this.handleMoreArtistClick}
              >
                {configJSON.seeMoreText}
              </SeeMoreButton>
            </SectionHeadingWrapper>

            <ArtistsSliderWrapper isEmpty={trendingArtists.length === 0}>
              <Loader wrapperHeight="100%" loading={isGettingArtists}>
                <LandingArtistCarousel
                  data-test-id="artists-carousel"
                  trendingArtists={trendingArtists}
                  handleUserProfileRedirection={
                    this.handleUserProfileRedirection
                  }
                />
              </Loader>
            </ArtistsSliderWrapper>
          </TrendingArtistsWrapper>

          <GetArtistMainWrapper component="section">
            <SectionHeadingWrapper>
              <img src={StepsIcon} alt="Icon" />
              <CustomHeading data-test-id="get-artists-heading">
                {configJSON.fourEasyStepsText}
              </CustomHeading>
            </SectionHeadingWrapper>

            <GetArtistsWrapper>
              {GET_ARTISTS_STEPS.map((stepInfo: ArtistStep, index: number) => (
                <React.Fragment key={stepInfo.heading}>
                  <ArtistStepsCard key={stepInfo.heading} {...stepInfo} />
                  {this.getStepsArrow(index)}
                </React.Fragment>
              ))}
            </GetArtistsWrapper>
          </GetArtistMainWrapper>

          <TestimonialsWrapper component="section">
            <SectionHeadingWrapper>
              <img src={TestimonialsIcon} alt="Icon" />
              <CustomHeading data-test-id="testimonials-heading">
                {configJSON.testimonialsText}
              </CustomHeading>
            </SectionHeadingWrapper>
            <Loader wrapperHeight="500px" loading={isGettingTestimonials}>
              <CustomEmptyResultInfo
                wrapperMargin="30px 50px"
                resultsLength={testimonials.length}
                message={configJSON.emptyTestimonialsMessage}
              >
                <TestimonialSwiperWrapper>
                  <LandingTestimonialsCarousel
                    data-test-id="testimonials-carousel"
                    testimonials={testimonials}
                    getBackgroundCards={this.getBackgroundCards}
                  />
                </TestimonialSwiperWrapper>
              </CustomEmptyResultInfo>
            </Loader>
          </TestimonialsWrapper>

          <GoogleAd
            responsive
            format="auto"
            sx={{ mt: 6 }}
            slot="8815326187"
          />
        </Box>
        <AppFooter navigation={navigation} />
      </>
    );
    // Customizable Area End
  }
}

// Customizable Area Start
const BannerSliderWrapper = styled("section")({
  position: "relative",
  width: "100%",
  aspectRatio: "8/3",
  minHeight: "220px",
  maxHeight: "85vh",
  overflow: "hidden",
});

const BannerSliderInnerWrapper = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  width: "100%",
});

const TrendingArtistsWrapper = styled(Box)({
  padding: "3rem 0",
});

const SectionHeadingWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "5px",
  position: "relative",
  margin: "0 4rem",
  [theme.breakpoints.down("md")]: {
    justifyContent: "flex-start",
    margin: "0 2rem",
  },
  [theme.breakpoints.down("sm")]: {
    marginRight: "0.5rem",
  },
}));

const SeeMoreButton = styled(Button)({
  position: "absolute",
  right: "0",
  fontSize: "14px",
  fontWeight: 400,
  textTransform: "none",
  borderRadius: "6px",
  padding: "2px 8px",
});

const CustomHeading = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  color: "#000",
  fontWeight: 700,
  lineHeight: "39px",
  [theme.breakpoints.down("md")]: {
    fontSize: "1.75rem",
    lineHeight: "33px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.35rem",
    lineHeight: "26px",
  },
}));

const ArtistsSliderWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isEmpty",
})<ArtistsSliderWrapperProps>(({ theme, isEmpty }) => ({
  height: "420px",
  width: "100%",
  marginTop: "20px",
  padding: "0 4rem",
  [theme.breakpoints.down("md")]: {
    paddingLeft: "2rem",
    paddingRight: isEmpty ? "2rem" : "0",
  },
}));

const GetArtistMainWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    justifyContent: "flex-start",
  },
}));

const GetArtistsWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  margin: "70px 4rem",
  gap: "10px",
  "& .down_arrow": {
    flexShrink: 1,
    margin: "-40px -40px 0px",
  },
  "& .up_arrow": {
    flexShrink: 1,
    alignSelf: "flex-end",
    margin: "0px -40px -40px",
  },
  "& img": {
    maxWidth: "249px",
    width: "100%",
    height: "auto",
  },
  [theme.breakpoints.down("md")]: {
    margin: "60px 2rem",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: "70px",
    alignItems: "center",
    margin: "50px 2rem",
    "& .down_arrow": {
      transform: "rotate(90deg)",
      alignSelf: "flex-end",
      margin: "-80px -60px -25px",
    },
    "& .up_arrow": {
      transform: "rotate(90deg)",
      alignSelf: "flex-start",
      margin: "-80px -60px -25px",
    },
    "& img": {
      maxWidth: "190px",
    },
  },
}));

const TestimonialsWrapper = styled(Box)({
  margin: "5rem 0px 2rem",
});

const TestimonialSwiperWrapper = styled(Box)({
  height: "525px",
  margin: "20px 0px",
  "& .custom-swiper-pagination": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    marginBottom: "35px",
  },
  "& .custom_bullet": {
    background: "#525252",
    borderRadius: "50%",
    width: "8px",
    height: "8px",
    fontSize: "14px",
    cursor: "pointer",
    opacity: 1,
  },
  "& .swiper-pagination-bullet-active": {
    background: "#652784",
    width: "14px",
    height: "14px",
  },
  "& .swiper-slide-active": {
    "& h5": {
      fontSize: "1.30rem",
    },
    "& h6": {
      fontSize: "1.10rem",
    },
    "& p": {
      fontSize: "1rem",
    },
    "& .avatar": {
      height: "102px",
      width: "102px",
    },
    "&:has(.c_white) .avatar": {
      marginTop: "-35px",
    },
    "@media (max-width: 414px)": {
      "& h5": {
        fontSize: "1.125rem",
      },
      "& h6": {
        fontSize: "0.9rem",
      },
      "& p": {
        fontSize: "0.8rem",
      },
      "& .avatar": {
        height: "82px",
        width: "82px",
      },
    },
  },
});
// Customizable Area End
